<template>
    <footer class="site-footer">
        <!-- светлые «крылья» в правом нижнем углу, как в макете -->
        <svg class="site-footer__wing" viewBox="0 0 420 260" aria-hidden="true">
            <path d="M420 40C330 120 210 205 60 260h360z" fill="#1a3f7f" fill-opacity=".45" />
            <path d="M420 150C350 175 270 200 170 230c90-6 170-18 250-40z" fill="#2a5aa6" fill-opacity=".35" />
        </svg>

        <div class="my-container relative">
            <div class="flex flex-col items-center pt-12 lg:pt-16">
                <NuxtLink :to="localePath('/')" class="block w-full max-w-[300px] sm:max-w-[440px] lg:max-w-[560px]">
                    <img src="/images/brand/logo-white.png" width="1486" height="153" alt="Qanot Sharq"
                        class="block w-full h-auto" />
                </NuxtLink>
                <!-- маршрут под логотипом: самолётик летит по пунктиру слева направо (SMIL — работает и при
                     «уменьшении движения» в системе, как парение самолёта на баннере), пройденный путь подсвечивается -->
                <svg class="site-footer__route" viewBox="0 0 540 40" aria-hidden="true">
                    <defs>
                        <mask id="footer-route-flown" maskUnits="userSpaceOnUse" x="0" y="0" width="540" height="40">
                            <path :d="ROUTE" pathLength="100" fill="none" stroke="#fff" stroke-width="8"
                                stroke-dasharray="100 100" stroke-dashoffset="100">
                                <animate attributeName="stroke-dashoffset" values="100;0;0" keyTimes="0;.85;1"
                                    calcMode="spline" keySplines=".45 0 .55 1;0 0 1 1" dur="9s"
                                    repeatCount="indefinite" />
                            </path>
                        </mask>
                    </defs>
                    <g fill="none" stroke-width="1.5">
                        <path :d="ROUTE" stroke="currentColor" stroke-dasharray="5 5" />
                        <path :d="ROUTE" stroke="#e3efff" stroke-dasharray="5 5" mask="url(#footer-route-flown)">
                            <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;.04;.82;.9;1" dur="9s"
                                repeatCount="indefinite" />
                        </path>
                        <circle cx="9" cy="22" r="5" stroke="currentColor" />
                        <circle cx="531" cy="22" r="5" stroke="currentColor" />
                    </g>
                    <!-- самолёт нарисован носом вверх; поворот на 90° — носом по ходу движения (rotate="auto") -->
                    <g opacity="0">
                        <path transform="rotate(90) scale(.9) translate(-12 -12)" fill="#e3efff"
                            d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                        <animateMotion :path="ROUTE" rotate="auto" keyPoints="0;1;1" keyTimes="0;.85;1"
                            calcMode="spline" keySplines=".45 0 .55 1;0 0 1 1" dur="9s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;.04;.82;.9;1" dur="9s"
                            repeatCount="indefinite" />
                    </g>
                </svg>
            </div>

            <!-- колонки разной ширины: длинные «Сверхнормативный багаж» и телефон не переносятся на 1024 -->
            <div
                class="grid grid-cols-2 lg:grid-cols-[1.1fr_1.15fr_0.8fr_1.2fr] gap-x-4 sm:gap-x-6 xl:gap-x-8 gap-y-8 sm:gap-y-10 pt-10 lg:pt-14 pb-10 lg:pb-14">
                <section aria-labelledby="footer-services">
                    <h2 id="footer-services" class="site-footer__title">{{ t('services') }}</h2>
                    <ul class="site-footer__links">
                        <!-- покупка — в прежней системе, как было на рабочем сайте (на время перехода на WX её не трогаем) -->
                        <li><a href="https://booking.qanotsharq.com/websky_grs/#/search" target="_blank" rel="noopener">{{ t('buyTicket') }}</a></li>
                        <li><NuxtLink :to="localePath('/extra-luggage')">{{ t('excessLuggage') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/information/on-board-service')">{{ t('orderFood') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/information/choosing-a-place')">{{ t('placeChoose') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/information/check-in-for-the-flight')">{{ t('registration') }}</NuxtLink></li>
                    </ul>
                </section>

                <section aria-labelledby="footer-information">
                    <h2 id="footer-information" class="site-footer__title">{{ t('information') }}</h2>
                    <ul class="site-footer__links">
                        <li><NuxtLink :to="localePath('/tariffs')">{{ t('tariffs') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/information/transportation-rules')">{{ t('shippingRules') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/information/baggage-transportation')">{{ t('luggageShipping') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/passenger-categories')">{{ t('passengerCategory') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/faq')">{{ t('aq') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/news')">{{ t('news') }}</NuxtLink></li>
                    </ul>
                </section>

                <section aria-labelledby="footer-company">
                    <h2 id="footer-company" class="site-footer__title">{{ t('company') }}</h2>
                    <ul class="site-footer__links">
                        <li><NuxtLink :to="localePath('/about')">{{ t('aboutUs') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/planes')">{{ t('ourFleet') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/vacancy')">{{ t('vacancy') }}</NuxtLink></li>
                        <li><NuxtLink :to="localePath('/magazine')">{{ t('ourJournals') }}</NuxtLink></li>
                    </ul>
                </section>

                <section aria-labelledby="footer-contacts" class="col-span-2 sm:col-span-1">
                    <h2 id="footer-contacts" class="site-footer__title">{{ t('contacts') }}</h2>
                    <a v-if="contacts.primary_phone" :href="`tel:+${contacts.primary_phone}`" class="site-footer__phone">
                        {{ formatPhoneNumber(contacts.primary_phone) }}
                    </a>
                    <a v-if="contacts.secondary_phone" :href="`tel:+${contacts.secondary_phone}`" class="site-footer__phone">
                        {{ formatPhoneNumber(contacts.secondary_phone) }}
                    </a>
                    <a v-if="contacts.email" :href="`mailto:${contacts.email}`" class="site-footer__email">
                        {{ contacts.email }}
                    </a>
                    <div class="flex gap-4 mt-6">
                        <a v-for="link in contacts.links" :key="link.link" :href="link.link" target="_blank"
                            rel="noopener" class="site-footer__social" :title="link.name" :aria-label="link.name">
                            <UIcon v-if="socialIcon(link.name)" :name="socialIcon(link.name)" class="w-6 h-6" />
                            <img v-else :src="link.icon" alt="" class="w-6 h-6 brightness-0 invert">
                        </a>
                    </div>
                </section>
            </div>

            <div class="site-footer__bottom">
                <div>© {{ year }} Qanot Sharq</div>
                <div class="flex items-center gap-5 sm:gap-8">
                    <NuxtLink :to="localePath('/privacy-policy')" class="site-footer__muted-link">
                        {{ t('confPolicy') }}
                    </NuxtLink>
                    <span class="hidden sm:block w-px h-9 bg-[#2f5594]"></span>
                    <button type="button" class="site-footer__top" @click="scrollTop">
                        {{ t('backToTop') }}
                        <UIcon name="heroicons:arrow-up-20-solid" class="w-5 h-5 text-[#3fb950]" />
                    </button>
                </div>
            </div>
        </div>
    </footer>
</template>

<script setup>
const localePath = useLocalePath()
const { t, locale } = useI18n()
const rc = useRuntimeConfig()
const headers = computed(() => ({
    lang: locale.value
}))

const { data: response } = await useAsyncData('contacts', () =>
    $fetch(rc.public.api + '/api/contacts', {
        headers: headers.value
    })
)

const contacts = computed(() => response.value?.data?.contacts || {})
// пунктир маршрута под логотипом: от левой точки к правой, по нему летит самолётик
const ROUTE = 'M14 22C80 12 170 6 250 18S440 30 526 22'
const year = new Date().getFullYear()

function formatPhoneNumber(phone) {
    return phone?.replace(/(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5');
}

// иконки соцсетей из админки — тёмные картинки; для известных сетей рисуем свои белые контурные
const SOCIAL_ICONS = {
    telegram: 'ri:telegram-2-fill',
    facebook: 'ri:facebook-circle-fill',
    instagram: 'ri:instagram-line'
}
function socialIcon(name) {
    return SOCIAL_ICONS[name?.trim().toLowerCase()]
}

function scrollTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<style>
.site-footer {
    position: relative;
    overflow: hidden;
    background: linear-gradient(160deg, #0c2550 0%, #0f2c5e 60%, #12336c 100%);
    color: #a9c5ee;
}

.site-footer__wing {
    position: absolute;
    right: 0;
    bottom: 0;
    width: min(420px, 60vw);
    height: auto;
    pointer-events: none;
}

.site-footer__route {
    display: block;
    width: min(380px, 75%);
    height: auto;
    margin-top: 24px;
    color: #8db7f0;
}

.site-footer__title {
    position: relative;
    padding-top: 22px;
    margin-bottom: 20px;
    font-family: Onest, Inter, sans-serif;
    font-size: 24px;
    line-height: 30px;
    font-weight: 600;
    color: #fff;
}

/* короткая зелёная черта над заголовком */
.site-footer__title::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40px;
    height: 3px;
    border-radius: 2px;
    background: #3fb950;
}

.site-footer__links {
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 17px;
    line-height: 22px;
}

.site-footer__links a,
.site-footer__muted-link {
    color: #a9c5ee;
    transition: color .15s;
}

.site-footer__links a:hover,
.site-footer__muted-link:hover {
    color: #fff;
}

.site-footer__phone {
    display: block;
    font-family: Onest, Inter, sans-serif;
    font-size: 28px;
    line-height: 34px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
}

.site-footer__phone:hover {
    color: #fff;
}

@media (min-width: 1024px) and (max-width: 1279px) {
    .site-footer__phone {
        font-size: 24px;
    }
}

/* на телефоне разделы по два в ряд — шрифт мельче, чтобы длинные пункты не ломались по слогам */
@media (max-width: 639px) {
    .site-footer__title {
        padding-top: 18px;
        margin-bottom: 14px;
        font-size: 20px;
        line-height: 26px;
    }

    .site-footer__links {
        gap: 11px;
        font-size: 15px;
        line-height: 20px;
    }
}

.site-footer__email {
    display: inline-block;
    margin-top: 8px;
    font-size: 18px;
    line-height: 24px;
    color: #5ea8f5;
}

.site-footer__email:hover {
    color: #8cc1ff;
}

.site-footer__social {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: 1px solid rgba(124, 172, 240, .55);
    border-radius: 9999px;
    color: #fff;
    transition: background-color .15s, border-color .15s;
}

.site-footer__social:hover {
    color: #fff;
    background: rgba(124, 172, 240, .15);
    border-color: rgba(124, 172, 240, .9);
}

.site-footer__bottom {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    gap: 16px;
    padding: 20px 0 24px;
    border-top: 1px solid rgba(94, 140, 214, .45);
    font-size: 15px;
    line-height: 20px;
}

@media (min-width: 640px) {
    .site-footer__bottom {
        flex-direction: row;
        justify-content: space-between;
    }
}

.site-footer__top {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border: 1px solid rgba(124, 172, 240, .55);
    border-radius: 9999px;
    color: #a9c5ee;
    transition: color .15s, border-color .15s;
}

.site-footer__top:hover {
    color: #fff;
    border-color: rgba(124, 172, 240, .9);
}
</style>
